// The editor's state: what is typed and chosen. Same rules as the original pages:
//   • up to 8 labels are kept, so switching layouts never loses typed data;
//   • a tab that no longer exists after a layout change falls back to Label 1;
//   • on the 4×6, turning the sticker moves a 2-product layout to the split that fits (stacked ↔ side by side);
//   • nothing is saved: leaving or refreshing the page clears it.
import { emptyProduct, SPLIT_FOR, type Orientation, type PageSpec, type Product, type Template } from '@/entities/label';

export interface EditorState {
  storeName: string;
  orient: Orientation;
  layout: string;
  template: Template;
  tab: number;
  products: Product[];
}

export type EditorAction =
  | { type: 'store'; value: string }
  | { type: 'orient'; value: Orientation }
  | { type: 'layout'; value: string }
  | { type: 'template'; value: Template }
  | { type: 'tab'; value: number }
  | { type: 'field'; idx: number; patch: Partial<Product> }
  | { type: 'copyFirst' };

export const initialEditor = (): EditorState => ({
  storeName: "Uncle Wiener's Wholesale", orient: 'portrait', layout: '1', template: 'classic', tab: 0,
  products: Array.from({ length: 8 }, emptyProduct),
});

export function editorReducer(spec: PageSpec) {
  return (s: EditorState, a: EditorAction): EditorState => {
    switch (a.type) {
      case 'store': return { ...s, storeName: a.value };
      case 'template': return { ...s, template: a.value };
      case 'tab': return { ...s, tab: a.value };
      case 'field': return { ...s, products: s.products.map((p, i) => (i === a.idx ? { ...p, ...a.patch } : p)) };
      case 'layout': {
        const layout = a.value;
        return { ...s, layout, tab: s.tab >= spec.count(layout) ? 0 : s.tab };
      }
      case 'orient': {
        // the 2-product layout that fits this orientation replaces the other one
        const valid = SPLIT_FOR[a.value];
        const layout = s.layout !== '1' && s.layout !== valid ? valid : s.layout;
        return { ...s, orient: a.value, layout, tab: s.tab >= spec.count(layout) ? 0 : s.tab };
      }
      case 'copyFirst': {
        const n = spec.count(s.layout);
        const first = s.products[0]!;
        return { ...s, products: s.products.map((p, i) => (i > 0 && i < n ? { ...first } : p)) };
      }
    }
  };
}

// Which layout buttons show: on the 4×6 only "1" and the split that fits the orientation; the letter sheet shows all.
export const layoutVisible = (spec: PageSpec, s: Pick<EditorState, 'orient'>, key: string) =>
  !spec.hasOrientation || key === '1' || key === SPLIT_FOR[s.orient];
